import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'

type Flag = {
  id: number
  key: string
  name: string
  description: string
  category: string
  is_enabled: boolean
  is_system: boolean
}

type FlagInput = {
  key: string
  name: string
  description: string
  category: string
  is_enabled: boolean
}

const emptyFlag: FlagInput = {
  key: '',
  name: '',
  description: '',
  category: '',
  is_enabled: false,
}

export default function FeatureFlags() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => (await api.get<Flag[]>('/feature-flags/')).data,
  })

  const [editing, setEditing] = useState<Flag | null>(null)
  const [creating, setCreating] = useState(false)

  const toggleMut = useMutation({
    mutationFn: async ({ id, is_enabled }: { id: number; is_enabled: boolean }) =>
      (await api.patch(`/feature-flags/${id}/`, { is_enabled })).data,
    onMutate: async ({ id, is_enabled }) => {
      await qc.cancelQueries({ queryKey: ['feature-flags'] })
      const previous = qc.getQueryData<Flag[]>(['feature-flags'])
      qc.setQueryData<Flag[]>(['feature-flags'], (old) =>
        (old || []).map((f) => (f.id === id ? { ...f, is_enabled } : f))
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['feature-flags'], ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['feature-flags'] }),
  })

  const createMut = useMutation({
    mutationFn: async (payload: FlagInput) =>
      (await api.post('/feature-flags/', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] })
      setCreating(false)
    },
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: FlagInput }) =>
      (await api.patch(`/feature-flags/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags'] })
      setEditing(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/feature-flags/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feature-flags'] }),
  })

  const flags = data || []

  // group by category
  const grouped: Record<string, Flag[]> = {}
  flags.forEach((f) => {
    const k = f.category || 'Other'
    if (!grouped[k]) grouped[k] = []
    grouped[k].push(f)
  })

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-gray-800">Feature Flags ({flags.length})</h2>
        <Button onClick={() => setCreating(true)}>+ Add flag</Button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Toggle individual features on or off. Use dotted keys like{' '}
        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">platform.audit_log</code> to namespace.
      </p>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : flags.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No flags yet.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, flagsInCat]) => (
            <div key={cat} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">{cat}</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {flagsInCat.map((f) => (
                  <div key={f.id} className="px-4 py-3 flex items-start justify-between hover:bg-gray-50">
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-800">{f.name}</span>
                        {f.is_system && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">system</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{f.key}</p>
                      {f.description && <p className="text-sm text-gray-600 mt-1">{f.description}</p>}
                    </div>
                    <div className="flex items-center space-x-3 shrink-0">
                      <Toggle
                        checked={f.is_enabled}
                        onChange={(v) => toggleMut.mutate({ id: f.id, is_enabled: v })}
                      />
                      <button onClick={() => setEditing(f)} className="text-blue-600 hover:underline text-sm">
                        Edit
                      </button>
                      {!f.is_system && (
                        <button
                          onClick={() => {
                            if (confirm(`Delete flag "${f.key}"?`)) deleteMut.mutate(f.id)
                          }}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <FlagDialog
          initial={
            editing
              ? {
                  key: editing.key,
                  name: editing.name,
                  description: editing.description,
                  category: editing.category,
                  is_enabled: editing.is_enabled,
                }
              : emptyFlag
          }
          isEdit={!!editing}
          keyLocked={editing?.is_system || false}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSubmit={(payload) => {
            if (editing) updateMut.mutate({ id: editing.id, payload })
            else createMut.mutate(payload)
          }}
        />
      )}
    </AdminLayout>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

function FlagDialog({
  initial,
  isEdit,
  keyLocked,
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  initial: FlagInput
  isEdit: boolean
  keyLocked: boolean
  onSubmit: (payload: FlagInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<FlagInput>(initial)
  const set = <K extends keyof FlagInput>(k: K, v: FlagInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit flag' : 'Add flag'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="flag-key">Key (dotted)</FieldLabel>
                <Input
                  id="flag-key"
                  type="text"
                  required
                  value={form.key}
                  onChange={(e) => set('key', e.target.value)}
                  className="font-mono"
                  placeholder="platform.audit_log"
                  disabled={keyLocked}
                />
                {keyLocked && (
                  <p className="text-xs text-gray-500 mt-1">System flags cannot have their key changed.</p>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="flag-name">Name</FieldLabel>
                <Input
                  id="flag-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Audit log"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="flag-category">Category</FieldLabel>
                <Input
                  id="flag-category"
                  type="text"
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  placeholder="Platform"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="flag-description">Description</FieldLabel>
                <Textarea
                  id="flag-description"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={2}
                />
              </Field>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.is_enabled}
                  onChange={(e) => set('is_enabled', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Enabled</span>
              </label>
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
